import { useState, useEffect, createRef, useRef } from 'react'
import TabSectionHeader from './TabSectionHeader'
import { useNavigate } from 'react-router-dom'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import {
  FormTextArea,
  FormDropDownList,
  FormNumericTextBox,
  FormDropDownListFilter,
  FormDateTimePicker,
  FormMultiSelect,
  FormCheckbox,
  FormInput,
} from '../FormComponents'

import { FaFilePdf, FaFileCsv, FaFileAlt, FaFileImage, FaExclamationTriangle, FaFlag, FaPaperclip, FaRegStickyNote, FaLink, FaFileInvoiceDollar, FaHammer, FaCoins, FaClipboardList, FaBriefcase, FaHandHoldingUsd, FaCheckSquare, FaRegSquare, FaCheck } from 'react-icons/fa'

import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout'

import {
  Upload,
  UploadOnStatusChangeEvent,
  UploadFileInfo,
  UploadFileStatus,
  UploadListItemProps,
  ExternalDropZone,
} from '@progress/kendo-react-upload'

import { getDocuments, getTargetJob, markJobsheetAsProcessed, getEventsForScheduler, deleteAllFutureJobs } from '../../../data/jobData'
import { getSitesDropdown } from '../../../data/siteData'
import { getClientsDropdown } from '../../../data/clientData'
import { getDepartmentDropdown } from '../../../data/departmentData'
import { getWorktypeDropdown } from '../../../data/worktypeData'
import { getNotifiableWorks, getJobNotifiableWorks, JobNotifiableWork } from '../../../data/notifiableWorkData'
import JobNotifiableWorks from './JobNotifiableWorks'
import { getEmployeeDropdown, getCurrentUserEmployee } from '../../../data/employeeData'
import { getQuoteDropdown, submitQuote } from '../../../data/quoteData'
import { getEnquirysourceDropdown } from '../../../data/enquirysourceData'
import { dropDown, fileDocumentTypes, Jobsheet, TargetJobInvoiceItems, JobVariation, Accrual, PostAccrual, UpdateAccrual, RetentionPayable, PostRetentionPayable, UpdateRetentionPayable } from '../../../data/interfaces'
import { Loader, LoaderType } from "@progress/kendo-react-indicators";
import QuoteDocumentRow from '../../QuoteDocumentRow'

import { DateFormatter, DateTimeFormatter } from '../../../data/helper'

import SkeletonForm from '../../Skeletons/SkeletonForm'
import Divider from '../../Divider/Divider'
import { useToast } from '../../../contexts/ToastContext'
import Notes from '../../../pages/Notes/Notes'
import { addNoteDTO } from '../../../data/notes';
import EntityLinks from '../Links/EntityLinks';
import AccrualsGrid from '../../grids/AccrualsGrid'
import AccrualForm from '../Accruals/AccrualForm'
import { getAccrualsByJob, createAccrual, updateAccrual, deleteAccrual } from '../../../data/accrualData'
import RetentionsPayableGrid from '../../grids/RetentionsPayableGrid'
import RetentionPayableForm from '../RetentionsPayable/RetentionPayableForm'
import { getRetentionsPayableByJob, createRetentionPayable, updateRetentionPayable, deleteRetentionPayable } from '../../../data/retentionPayableData'
import JobsRemidners from './JobsReminders'
import { JobReminder, completeServiceReminder, deleteServiceReminder, getJobReminders } from '../../../data/serviceReminderData'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import EditServiceRemindersForm from '../ServiceReminders/EditServiceRemindersForm'
import { Label } from '@progress/kendo-react-labels'
import dayjs from 'dayjs'
import { getAddressesForClientDropdown, getClientsForClientsDropdown, getClientByIdV2, getContactById } from '../../../data/accountsData'
import { Switch } from '@progress/kendo-react-inputs'
import { MultiSelect } from '@progress/kendo-react-dropdowns'
import { getDocument, downloadDocument } from '../../../data/fileData'
import QuotesPopup from '../../QuotesPopup'
import JobVariations from './JobVariations'
import SubContracts from './SubContracts'
import WorkingSectionsView, { WorkingSectionsViewHandle } from '../../grids/WorkingSectionsView'
import WorkingsAdjustmentSummary from '../../grids/WorkingsAdjustmentSummary'
import { MaterialWorking, LabourWorking } from '../../../data/interfaces'
import { 
  getMaterialWorkingsByEnquiry, 
  getMaterialWorkingsByJob,
  getLabourWorkingsByEnquiry,
  getLabourWorkingsByJob,
  createMaterialWorking,
  updateMaterialWorking,
  deleteMaterialWorking,
  createLabourWorking,
  updateLabourWorking,
  deleteLabourWorking
} from '../../../data/workingsData'
import SiteReportsTab, { SiteReportsTabHandle } from '../../grids/SiteReportsTab'
import JobPurchaseOrders from './JobPurchaseOrders'
import { getPurchaseOrdersByJob } from '../../../data/purchaseOrderData'

const apiURL = process.env.REACT_APP_API_URL

/**
 * Renders a materials quantity without trailing zeroes, so a whole number reads
 * as "2" rather than "2.00" while a part quantity keeps its decimals.
 */
const formatQuantity = (quantity: number): string =>
  Number.isInteger(quantity) ? String(quantity) : String(parseFloat(quantity.toFixed(2)))

/**
 * The estimated labour for a jobsheet's remedial works, as the engineer entered
 * it. Days and hours are separate figures rather than a total, so they are shown
 * that way; an estimate of nothing at all reads as a dash.
 */
const formatRemedialLabour = (jobsheet: Jobsheet): string => {
  const days = jobsheet.remedialLabourDays || 0
  const hours = jobsheet.remedialLabourHours || 0

  const parts: string[] = []
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)

  return parts.length > 0 ? parts.join(', ') : '-'
}

const EditJobForm = (props: any) => {
  const dateFormatter = new DateFormatter()
  const dateTimeFormatter = new DateTimeFormatter()
  const { showToast } = useToast()

  const [isDeleted, setIsDeleted] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [hasFinancialStatementPermission, setHasFinancialStatementPermission] = useState(false)
  const [hasPurchaseOrderPermission, setHasPurchaseOrderPermission] = useState(false)
  const [purchaseOrderCount, setPurchaseOrderCount] = useState(0)

  const [siteData, setSiteData] = useState<dropDown[]>([])
  const [siteDataOrig, setSiteDataOrig] = useState<dropDown[]>([])
  const [clientData, setClientData] = useState<dropDown[]>([])
  const [clientDataOrig, setClientDataOrig] = useState<dropDown[]>([])
  const [enquirysourceData, setEnquirysourceData] = useState<dropDown[]>([])
  const [departmentData, setDepartmentData] = useState<dropDown[]>([])
  const [worktypeData, setWorktypeData] = useState<dropDown[]>([])
  const [notifiableWorksOptions, setNotifiableWorksOptions] = useState<string[]>([])
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  const [employeeProjectData, setEmployeeProjectData] = useState<dropDown[]>([])
  const [employeeDataOrig, setEmployeeDataOrig] = useState<dropDown[]>([])
  const [quoteId, setQuoteId] = useState<number>(0)

  const [buttonSpinner, setButtonSpinner] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  // The job's notifiable works with their documents, for the rail's Notifiable
  // Works section, and which one is open.
  const [jobNotifiableWorks, setJobNotifiableWorks] = useState<JobNotifiableWork[]>([])
  const [selectedNotifiableWork, setSelectedNotifiableWork] = useState<string | null>(null)
  const [files, setFiles] = useState<UploadFileInfo[]>([])
  const [documentDataDisplayed, setDocumentDataDisplayed] = useState(false)
  const [variations, setVariations] = useState<JobVariation[]>([])
  const [materialWorkings, setMaterialWorkings] = useState<MaterialWorking[]>([])
  const [labourWorkings, setLabourWorkings] = useState<LabourWorking[]>([])
  const token = localStorage.getItem('authToken')

  // Accruals state
  const [accruals, setAccruals] = useState<Accrual[]>([])
  const [accrualDialogVisible, setAccrualDialogVisible] = useState(false)
  const [selectedAccrual, setSelectedAccrual] = useState<Accrual | null>(null)

  // Retentions payable state
  const [retentionsPayable, setRetentionsPayable] = useState<RetentionPayable[]>([])
  const [retentionPayableDialogVisible, setRetentionPayableDialogVisible] = useState(false)
  const [selectedRetentionPayable, setSelectedRetentionPayable] = useState<RetentionPayable | null>(null)

  const [reminderDialogVisible, setReminderDialogVisible] = useState(false)
  const [bookingCheckDialogVisible, setBookingCheckDialogVisible] = useState(false)
  const [hasFutureBookings, setHasFutureBookings] = useState(false)
  const [pendingCompleteRedirect, setPendingCompleteRedirect] = useState(false)

  const history = useNavigate()

  const [projectUrlAlreadySet, setProjectUrlAlreadySet] = useState(false);
  const [folderStructureToggleSet, setFolderStructureToggleSet] = useState(false);
  const [isBadDebter, setIsBadDebter] = useState(false);


  const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([
    {
      documentId: 0,
      fileName: '',
      fileExtension: '',
      appCanView: false,
    },
  ])

  const [formState, setFormState] = useState({
    invoiceItems: [] as TargetJobInvoiceItems[],
    contact: {
      contactId: 0,
      title: "",
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
    },
    site: {
      siteId: 0,
      siteAddr: "",
      postcode: "",
    },
    jobsheets: [] as Jobsheet[],
    quoteId: 0,
    enquiryId: 0,
    bookedStartDate: '',
    bookedEndDate: '',
    quoteRef: 0,
    enquiryRef: 0,
    refFormatted: '',
    siteAddr: {
      text: '',
      value: 0,
    },
    clientName: {
      text: '',
      value: 0,
    },
    enquirysourceId: {
      text: '',
      value: 0,
    },
    description: '',
    purchaseOrder: '',
    externallyBooked: false,
    externallyBookedName: '',
    notifiableWorks: [] as string[],
    worktypeId: {
      text: '',
      value: 0,
    },
    cost: 0,
    proformaCost: 0,
    costsToDate: 0,
    committedCosts: 0,
    amountBilledToDate: 0,
    amountLeftToBill: 0,
    firstHourInvoiced: false,
    firstHourInvoicedOriginal: false,
    exportedDate: '',
    createdDate: '',
    isExported: false,
    startDate: '',
    endDate: '',
    scheduledStartDate: '',
    scheduledEndDate: '',
    completedDate: '',
    deletedDate: '',
    employeeProjectmanager: {
      text: '',
      value: 0,
    },
    quote: {
      quoteId: 0,
      ref: 0,
      refFormatted: '',
    },
    quotes: [] as { quoteId: number, ref: number, refFormatted: string, description?: string, cost?: number, wonDate?: string | null }[],
    jobEmployees: [{ text: '', value: 0 }],
    projectFolderUrl: null,
    variations: [] as JobVariation[],
    inDefect: false,
    enableSubcontractAgreements: false,
    workingsAdjustmentPercent: null as number | null,
  })

  const [loading, setLoading] = useState(true)

  const jobId = props.onJobId

  // Loaded with the job. Saving the job closes the panel, so a job reopened
  // after its notifiable works changed loads them afresh.
  const loadJobNotifiableWorks = async () => {
    if (!jobId) return
    try {
      setJobNotifiableWorks(await getJobNotifiableWorks(jobId))
    } catch (error) {
      console.error('Error loading notifiable works for job:', error)
    }
  }

  useEffect(() => {
    loadJobNotifiableWorks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  // The rail's Purchase Orders badge. The tab reports the count too, but the
  // tab strip only builds a tab once it is opened, so without this the badge
  // stayed blank until someone clicked it. Only fetched for users who can see
  // purchase orders - the item isn't in the rail otherwise.
  useEffect(() => {
    if (!jobId || !hasPurchaseOrderPermission) return
    getPurchaseOrdersByJob(jobId)
      .then((orders) => setPurchaseOrderCount(orders.length))
      .catch((error) => console.error('Error loading purchase order count:', error))
  }, [jobId, hasPurchaseOrderPermission])


  const checkForProjectUrl = () => {
    const url = formState.projectFolderUrl;
    if (url !== null) {
      const path: string = url;
      if (path.includes("https://")) {
        return <a href={path} target="_blank" rel="noreferrer">View folder structure</a>
      } else {
        return <div><strong>Generating folder structure... Please check back soon.</strong></div>
      }
    }
  }
  const fetchPlaceholderData = async () => {
    // Reset button spinner when loading job data
    setButtonSpinner(false)
    
    let job = await getTargetJob(jobId)
    // Surface the friendly job reference to the drawer header.
    props.onJobRefLoaded?.(job.refFormatted)
    setFormState({
      invoiceItems: job.invoiceItems,
      contact: {
        contactId: job.contact.contactId,
        title: job.contact.title ?? '',
        firstname: job.contact.firstname ?? '',
        lastname: job.contact.lastname ?? '',
        phone: job.contact.phone ?? '',
        email: job.contact.email ?? '',
      },
      site: {
        siteId: job.site.siteId ?? 0,
        siteAddr: job.site.siteAddr ?? '',
        postcode: job.site.postcode ?? '',
      },
      jobsheets: job.jobsheets,
      enquiryId: job.enquiryId,
      quoteId: job.quote.quoteId as number,
      bookedEndDate: job.bookedEndDate,
      bookedStartDate: job.bookedStartDate,
      quoteRef: job.quoteRef,
      enquiryRef: job.enquiryRef,
      refFormatted: job.refFormatted,
      siteAddr: {
        text: job.site.siteAddr + ", " + job.site.postcode,
        value: job.site.siteId,
      },
      clientName: {
        text: job.client.clientName + " - " + job.client.accountRef,
        value: job.client.clientId,
      },
      enquirysourceId: {
        text: job.enquirysource.description,
        value: job.enquirysource.enquirysourceId || 0,
      },
      description: job.description,
      purchaseOrder: job.purchaseOrder,
      externallyBooked: job.externallyBooked || false,
      externallyBookedName: job.externallyBookedName || '',
      worktypeId: {
        text: job.worktype.description,
        value: job.worktype.worktypeId,
      },
      cost: job.cost,
      proformaCost: job.proformaCost,
      costsToDate: job.costsToDate,
      committedCosts: job.committedCosts,
      amountBilledToDate: job.amountBilledToDate,
      amountLeftToBill: job.amountLeftToBill,
      firstHourInvoiced: job.firstHourInvoiced,
      firstHourInvoicedOriginal: job.firstHourInvoiced,
      exportedDate: job.exportedDate,
      createdDate: job.createdDate,
      isExported: job.exportedDate !== null && job.exportedDate !== '',
      startDate: job.startDate,
      endDate: job.endDate,
      scheduledStartDate: job.scheduledStartDate,
      scheduledEndDate: job.scheduledEndDate,
      completedDate: job.completedDate,
      deletedDate: job.deletedDate,
      employeeProjectmanager: {
        text: job.employeeProjectmanager.employeeName,
        value: job.employeeProjectmanager.employeeId,
      },
      quote: {
        quoteId: job.quote.quoteId || 0,
        ref: job.quote.ref || 0,
        refFormatted: job.quote.refFormatted || 'No quote associated with the selected job.',
      },
      quotes: job.quotes || [],
      jobEmployees: job.employees.map((employee) => ({
        text: employee.employeeName,
        value: employee.employeeId,
      })),
      projectFolderUrl: job.projectFolderUrl,
      variations: job.variations || [],
      notifiableWorks: job.notifiableWorks ? job.notifiableWorks.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      inDefect: job.inDefect || false,
      // No longer decides anything - Sub-Contracts is always available. It is
      // still read and sent back so saving a job leaves the stored value alone;
      // the API assigns this column unconditionally, so dropping it here would
      // quietly null it on every job that gets edited.
      enableSubcontractAgreements: job.enableSubcontractAgreements || false,
      workingsAdjustmentPercent: job.workingsAdjustmentPercent ?? null,
    })
    setVariations(job.variations || [])
    setProjectUrlAlreadySet(job.projectFolderUrl !== null ? true : false)

    // Check if client is a bad debter
    if (job.client?.clientId) {
      const clientData = await getClientByIdV2(job.client.clientId);
      setIsBadDebter(clientData.badDebter || false);
    }

    setIsDeleted(job.deletedDate !== null)

    if (job.completedDate !== null) {
      setIsCompleted(true)
    } else {
      setIsCompleted(false)
    }

    setLoading(false)
  }

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        console.log('Current user:', currentUser)
        console.log('Current user permissions:', currentUser?.permissions)
        if (currentUser && currentUser.permissions) {
          const hasPermission = currentUser.permissions.some(
            (p) => {
              console.log('Checking permission:', p)
              return p.area === 'Reports' && p.permission === 'FinancialStatement' && p.assigned
            }
          )
          console.log('Has Financial Statement permission:', hasPermission)
          setHasFinancialStatementPermission(hasPermission)

          // Purchase orders are security trimmed: no permission, no rail item.
          // Edit implies view, as elsewhere in the portal.
          setHasPurchaseOrderPermission(
            currentUser.permissions.some(
              (p) =>
                p.assigned &&
                ((p.area === 'Portal' && p.permission === 'Administrator') ||
                  (p.area === 'PurchaseOrders' &&
                    (p.permission === 'View' || p.permission === 'Edit')))
            )
          )
        } else {
          console.warn('No permissions found for current user')
          // Default to false if no permissions
          setHasFinancialStatementPermission(false)
          setHasPurchaseOrderPermission(false)
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        setHasFinancialStatementPermission(false)
        setHasPurchaseOrderPermission(false)
      }
    }
    checkPermissions()
  }, [])

  useEffect(() => {
    setWorktypeData(props.updatedWorkTypes)
  }, [props.updatedWorkTypes])

  useEffect(() => {
    // Reset button spinner when form loads/reloads
    setButtonSpinner(false);
    
    const fetchSiteData = async () => {
      const siteData = await getSitesDropdown('')
      setSiteData(siteData)
      setSiteDataOrig(siteData)
    }

    const fetchClientData = async () => {
      const clientData = await getClientsDropdown('')
      setClientData(clientData)
      setClientDataOrig(clientData)
    }

    const fetchEnquirysourceData = async () => {
      const enquirysourceData = await getEnquirysourceDropdown()
      setEnquirysourceData(enquirysourceData)
    }

    const fetchDepartmentData = async () => {
      const departmentData = await getDepartmentDropdown()
      setDepartmentData(departmentData)
    }

    const fetchWorktypeData = async () => {
      const worktypeData = await getWorktypeDropdown()
      setWorktypeData(worktypeData)
    }

    const fetchNotifiableWorks = async () => {
      const notifiableWorks = await getNotifiableWorks()
      setNotifiableWorksOptions(notifiableWorks)
    }

    const fetchEmployeeData = async () => {
      const employeeData = await getEmployeeDropdown('')
      setEmployeeData(employeeData)
      setEmployeeDataOrig(employeeData)
    }



    fetchSiteData();
    fetchClientData();
    fetchDepartmentData();
    fetchWorktypeData();
    fetchNotifiableWorks();
    fetchEmployeeData();
    fetchEnquirysourceData();
    fetchPlaceholderData();
    
    // Fetch workings data if we have a jobId
    if (jobId && jobId > 0) {
      fetchWorkingsData(jobId);
      fetchAccrualsData();
      fetchRetentionsPayableData();
    }
  }, [jobId])

  const fetchDocumentData = async (jobId: number, quoteId: number) => {
    await getDocuments(jobId, quoteId)
      .then((data) => {
        console.log('EditJobForm - Fetched document data:', data);
        data.forEach(doc => {
          console.log(`Document ${doc.fileName}: appCanView = ${doc.appCanView}`);
        });
        setDocumentData(data)
      })

  }

  const fetchWorkingsData = async (jobId: number) => {
    try {
      const [materialData, labourData] = await Promise.all([
        getMaterialWorkingsByJob(jobId),
        getLabourWorkingsByJob(jobId)
      ]);
      
      setMaterialWorkings(materialData);
      setLabourWorkings(labourData);
    } catch (error) {
      console.error('Error fetching workings data:', error);
      showToast('Error loading workings data', 'error');
    }
  }

  useEffect(() => {
    setDocumentDataDisplayed(true)
  }, [documentData])

  useEffect(() => {
    setQuoteId(formState.quote.quoteId)
  }, [formState])

  useEffect(() => {
    if (jobId && jobId > 0) {
      fetchDocumentData(jobId, quoteId)
    }
  }, [jobId, quoteId])


  //Handle submits
  const handleSubmit = (dataToSubmit: any) => {
    setButtonSpinner(true)
    deleteReminders()
    // The adjustment percentage is held in formState rather than as a Kendo
    // Field, because it sits inside the workings layout rather than the job
    // form. Kendo only submits what it manages, so fold it in here.
    props.onEditSubmit(
      {
        ...dataToSubmit,
        workingsAdjustmentPercent: formState.workingsAdjustmentPercent,
      },
      jobId
    )
  }

  const redirectToCreateNewServiceReminder = () => {
    history(`/service-reminders/add?client=${encodeURIComponent(formState.clientName.text.split(" - ")[0])}&site=${encodeURIComponent(formState.siteAddr.text)}&workType=${encodeURIComponent(formState.worktypeId.text)}`)
  }

  const handleCreateQuote = async () => {
    setButtonSpinner(true)
    try {
      const newQuote = await submitQuote({
        clientId: formState.clientName.value,
        siteId: formState.siteAddr.value,
        contactId: formState.contact.contactId,
        enquirysourceId: formState.enquirysourceId.value,
        worktypeId: formState.worktypeId.value,
        employeeAssignedToId: formState.employeeProjectmanager.value,
        description: `Remedial work following ${formState.refFormatted}`,
        priorityId: 1,
        cost: 0,
        followupDate: new Date(Date.now() + 86400000).toISOString(),
        enquiryId: null,
        requireFollowups: false,
      }) as any
      history(`/quotes?id=${newQuote.quoteId}`)
    } catch (e) {
      console.error('Failed to create quote from job', e)
    } finally {
      setButtonSpinner(false)
    }
  }

  const completeJobHandler = async (redirect: boolean) => {
    // Check if job has future bookings
    try {
      const scheduledEvents = await getEventsForScheduler(false, false)
      const jobBookings = scheduledEvents.filter(event => event.jobId === jobId)
      const now = new Date()
      const futureBookings = jobBookings.filter(booking => new Date(booking.end) > now)
      
      if (futureBookings.length > 0) {
        setHasFutureBookings(true)
        setPendingCompleteRedirect(redirect)
        setBookingCheckDialogVisible(true)
        return
      }
    } catch (error) {
      console.error('Error checking future bookings:', error)
    }
    
    // If no future bookings, show reminder dialog
    setReminderDialogVisible(true)
  }

  const handleCancelFutureBookingsAndComplete = async () => {
    try {
      await deleteAllFutureJobs(jobId)
      setBookingCheckDialogVisible(false)
      setHasFutureBookings(false)
      showToast('Future bookings cancelled', 'success')
      // After cancelling bookings, show the reminder dialog
      setReminderDialogVisible(true)
    } catch (error) {
      console.error('Error cancelling future bookings:', error)
      showToast('Error cancelling future bookings', 'error')
      setBookingCheckDialogVisible(false)
    }
  }

  const handleCompleteWithoutCancelling = () => {
    setBookingCheckDialogVisible(false)
    setHasFutureBookings(false)
    // Proceed to reminder dialog without cancelling bookings
    setReminderDialogVisible(true)
  }

  const deleteJobHandler = () => {
    props.onJobDeleteSubmit(jobId)
    deleteReminders()
  }

  const reinstateDeletedJobHandler = () => {
    const updatedFormState = {
      ...formState,
      deletedDate: '',
    }

    handleSubmit(updatedFormState);
  }

  const reinstateCompletedJobHandler = () => {
    const updatedFormState = {
      ...formState,
      completedDate: '',
    }

    handleSubmit(updatedFormState);
  }


  //Search filters
  const searchSiteDropdown = (searchTerm: string) => {
    const fetchSiteData = async (search: string) => {
      await getSitesDropdown(search).then((result) => {
        setSiteData(result)
      })
    }

    fetchSiteData(searchTerm)
  }

  const searchClientDropdown = (searchTerm: string) => {
    const fetchClientData = async (search: string) => {
      await getClientsDropdown(search).then((result) => {
        setClientData(result)
      })
    }

    fetchClientData(searchTerm)
  }

  const searchEmployeeDropdown = (searchTerm: string) => {
    const fetchEmployeeData = async (search: string) => {
      await getEmployeeDropdown(search).then((result) => {
        setEmployeeData(result)
      })
    }

    fetchEmployeeData(searchTerm)
  }

  const searchEmployeeProjectDropdown = (searchTerm: string) => {
    const fetchEmployeeData = async (search: string) => {
      await getEmployeeDropdown(search).then((result) => {
        setEmployeeProjectData(result)
      })
    }

    fetchEmployeeData(searchTerm)
  }

  const handleAddClient = () => {
    props.onAddClientToggle()
  }
  const handleAddSite = () => {
    props.onAddSiteToggle()
  }
  const handleAddWorktype = () => {
    props.onAddWorktypeToggle()
  }

  const handleSelectedTab = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
    fetchDocumentData(jobId, quoteId);
  }

  // Accrual handlers
  const fetchAccrualsData = async () => {
    try {
      const accrualsData = await getAccrualsByJob(jobId)
      setAccruals(accrualsData)
    } catch (error) {
      console.error('Error fetching accruals:', error)
      showToast('Failed to load accruals', 'error')
    }
  }

  const handleAddAccrual = () => {
    setSelectedAccrual(null)
    setAccrualDialogVisible(true)
  }

  const handleEditAccrual = (accrual: Accrual) => {
    setSelectedAccrual(accrual)
    setAccrualDialogVisible(true)
  }

  const handleDeleteAccrual = async (accrualId: number) => {
    try {
      await deleteAccrual(accrualId)
      showToast('Accrual deleted successfully', 'success')
      fetchAccrualsData()
    } catch (error) {
      console.error('Error deleting accrual:', error)
      showToast('Failed to delete accrual', 'error')
    }
  }

  const handleAccrualSubmit = async (accrualData: PostAccrual | UpdateAccrual) => {
    try {
      if ('accrualId' in accrualData) {
        // Update existing accrual
        await updateAccrual(accrualData.accrualId, accrualData)
        showToast('Accrual updated successfully', 'success')
      } else {
        // Create new accrual
        await createAccrual(accrualData)
        showToast('Accrual created successfully', 'success')
      }
      setAccrualDialogVisible(false)
      setSelectedAccrual(null)
      fetchAccrualsData()
    } catch (error) {
      console.error('Error saving accrual:', error)
      showToast('Failed to save accrual', 'error')
    }
  }

  const handleAccrualCancel = () => {
    setAccrualDialogVisible(false)
    setSelectedAccrual(null)
  }

  // Retention payable handlers
  const fetchRetentionsPayableData = async () => {
    try {
      const retentionsPayableData = await getRetentionsPayableByJob(jobId)
      setRetentionsPayable(retentionsPayableData)
    } catch (error) {
      console.error('Error fetching retentions payable:', error)
      showToast('Failed to load retentions payable', 'error')
    }
  }

  const handleAddRetentionPayable = () => {
    setSelectedRetentionPayable(null)
    setRetentionPayableDialogVisible(true)
  }

  const handleEditRetentionPayable = (retentionPayable: RetentionPayable) => {
    setSelectedRetentionPayable(retentionPayable)
    setRetentionPayableDialogVisible(true)
  }

  const handleDeleteRetentionPayable = async (retentionPayableId: number) => {
    try {
      await deleteRetentionPayable(retentionPayableId)
      showToast('Retention payable deleted successfully', 'success')
      fetchRetentionsPayableData()
    } catch (error) {
      console.error('Error deleting retention payable:', error)
      showToast('Failed to delete retention payable', 'error')
    }
  }

  const handleRetentionPayableSubmit = async (retentionPayableData: PostRetentionPayable | UpdateRetentionPayable) => {
    try {
      if ('retentionPayableId' in retentionPayableData) {
        // Update existing retention payable
        await updateRetentionPayable(retentionPayableData.retentionPayableId, retentionPayableData)
        showToast('Retention payable updated successfully', 'success')
      } else {
        // Create new retention payable
        await createRetentionPayable(retentionPayableData)
        showToast('Retention payable created successfully', 'success')
      }
      setRetentionPayableDialogVisible(false)
      setSelectedRetentionPayable(null)
      fetchRetentionsPayableData()
    } catch (error) {
      console.error('Error saving retention payable:', error)
      showToast('Failed to save retention payable', 'error')
    }
  }

  const handleRetentionPayableCancel = () => {
    setRetentionPayableDialogVisible(false)
    setSelectedRetentionPayable(null)
  }

  //#region File Upload
  const uploadRef = createRef<Upload>()
  const workingSectionsRef = useRef<WorkingSectionsViewHandle>(null)
  const siteReportsRef = useRef<SiteReportsTabHandle>(null)

  // NOTE: document loading is handled by the [jobId, quoteId] effect (which uses
  // the job-aware getDocuments endpoint) and refreshed after upload in
  // handleUploadSuccess. A previous effect here refetched via getQuoteDocuments
  // on every `files` change, which returned quote-only documents (empty for jobs
  // without a quote) and blanked the list right after an upload.

  const icon = (fileName: string) => {
    if (fileName.includes('.pdf')) {
      return (
        <FaFilePdf
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    } else if (fileName.includes('.csv')) {
      return (
        <FaFileCsv
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    } else if (fileName.includes('.txt')) {
      return (
        <FaFileAlt
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    } else if (fileName.includes('.jpg')) {
      return (
        <FaFileImage
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    } else if (fileName.includes('.png')) {
      return (
        <FaFileImage
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    } else {
      return (
        <FaFileAlt
          style={{
            fontSize: '20pt',
            color: 'rgb(33, 67, 59)',
            marginRight: '1rem',
          }}
        />
      )
    }
  }

  const CustomListItemUI = (props: UploadListItemProps) => {
    return (
      <ul>
        {props.files.map((file) => (
          <div
            style={{ fontSize: '10pt', fontFamily: 'Heebo, Sans-Serif' }}
            key={file.name}
          >
            {icon(file.name)}
            {file.name}
          </div>
        ))}
      </ul>
    )
  }

  const handleDocumentDelete = (documentId: number) => {
    props.onDocumentDelete(documentId)
    setDocumentData(
      documentData.filter((document: any) => document.documentId !== documentId)
    )
  }

  const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState)

    // onStatusChange also fires when an upload starts or fails, so only act on
    // files that actually finished uploading. Refresh the document list so the
    // newly uploaded file appears without needing to reload the form.
    const uploaded = event.affectedFiles.some(
      (file) => file.status === UploadFileStatus.Uploaded
    )
    const failed = event.affectedFiles.some(
      (file) => file.status === UploadFileStatus.UploadFailed
    )

    if (uploaded) {
      showToast('Succesfully uploaded new document', 'success')
      fetchDocumentData(jobId, quoteId)
    } else if (failed) {
      showToast('Failed to upload document', 'error')
    }
  }
  //#endregion

  const addNoteDTO: addNoteDTO = {
    enquiryId: null,
    quoteId: null,
    jobId: jobId
  };


  /**************************************************
   * reminders
   */
  interface Reminders {
    id: number;
    checked: boolean;
  }
  const [reminders, setReminders] = useState<Reminders[]>([]);
  const [jobReminders, setJobReminders] = useState<JobReminder[]>([])

  const handleCompleteReminder = async (id: number, checked: boolean) => {
    setReminders((prev) => {
      const updatedReminders = prev.map((reminder) => {
        if (reminder.id === id) {
          return { ...reminder, checked };
        }
        return reminder;
      });
      const existingReminder = prev.find((reminder) => reminder.id === id);
      if (!existingReminder) {
        return [...prev, { id, checked }];
      }
      return updatedReminders;
    });
  }

  const getReminders = async () => {
    const reminders = await getJobReminders(formState.clientName.value, formState.siteAddr.value);
    setJobReminders(reminders);
  }

  useEffect(() => {
    if (formState.clientName.value !== 0 && formState.siteAddr.value !== 0) {
      getReminders()
    }
  }, [formState.clientName.value, formState.siteAddr.value])

  const deleteReminders = async () => {
    reminders.forEach(async (reminder) => {
      deleteServiceReminder(reminder.id, jobId).catch(err => console.log(err));
    });
  }

  const formatScheduleDate = (date: string) => {
    return !date ? "Date not set" : dayjs(date).format("DD-MM-YYYY HH:mm");
  }

  const router = useNavigate();


  const [accountData, setAccountData] = useState<dropDown[]>([]);


  const getAndStoreAddressesForCustomer = async (customerId: number) => {
    try {
      const res = await getAddressesForClientDropdown(customerId);
      setSiteData(res);
      setFormState((prev) => ({
        ...prev,
        siteAddr: {
          text: res.length === 1 ? res[0]?.text || '' : formState.siteAddr.text,
          value: res.length === 1 ? res[0]?.value || 0 : formState.siteAddr.value,
        }
      }))
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (formState.clientName.value !== 0) {
      getAndStoreAddressesForCustomer(formState.clientName.value);
    }
  }, [formState.clientName])

  /**
   * End Account and address selection
   */

  // Contacts for client
  const [contactsForClient, setContactsForClient] = useState<dropDown[]>([]);

  // The contact dropdown only carries a name and id, so pull the full record to
  // keep the details header in step with the selection.
  const refreshContactDetails = async (contactId: number) => {
    if (!contactId) return
    try {
      const contact = await getContactById(contactId)
      setFormState((prev: any) => ({
        ...prev,
        contact: {
          ...prev.contact,
          contactId,
          title: contact.title ?? '',
          firstname: contact.firstname ?? '',
          lastname: contact.lastname ?? '',
          phone: contact.phone ?? '',
          email: contact.email ?? '',
        }
      }))
    } catch (err) {
      console.log(err)
    }
  }

  const getAndStoreContactsForCustomer = async (customerId: number) => {
    try {
      const res = await getClientsForClientsDropdown(customerId);
      setContactsForClient(res);
      setFormState((prev: any) => ({
        ...prev,
        // Spread the existing contact so the loaded name/phone/email survive -
        // this fires on load as soon as the client is set, and replacing the
        // object wholesale would blank the details header.
        contact: {
          ...prev.contact,
          contactId: res.length === 1 ? res[0]?.value || 0 : prev.contact.contactId,
        }
      }))

    } catch (err) {
      console.log(err)
    }
  }

  const setJobsheetAsProcessed = async (jobsheetId: number) => {
    try {
      const res = await markJobsheetAsProcessed(jobsheetId);
      showToast("Jobsheet updated", "success");
      fetchPlaceholderData();
    } catch (err) {
      showToast("There was a problem updating the jobsheet", "error");
      console.log(err)
    }
  }

  useEffect(() => {
    if (formState.clientName.value !== 0) {
      getAndStoreContactsForCustomer(formState.clientName.value);
    }
  }, [formState.clientName])

  // Single source of truth for the tab order. Kendo TabStrip assigns each
  // rendered <TabStripTab> a numeric index in DOM order, and it filters out
  // falsy children — so when lockEdit hides File Upload every later index
  // shifts down by one. This array mirrors that exactly, and the rail below
  // maps a section id -> index via indexOf, so the two can never drift apart.
  const jobTabOrder = [
    'jobForm',
    'jobsheet',
    'invoices',
    // Security trimmed, so the tab is only rendered - and only occupies an
    // index - when the user holds a PurchaseOrders permission.
    ...(hasPurchaseOrderPermission ? ['purchaseOrders'] : []),
    ...(props.lockEdit ? [] : ['files']),
    'notes',
    'links',
    'variations',
    'workings',
    'accruals',
    'retentionsPayable',
    'siteReports',
    'subcontracts',
    // Always rendered, so it never shifts the tabs before it; the rail only
    // links to it when the job has notifiable works.
    'notifiableWorks',
  ]

  const jobNavGroups = [
    {
      label: 'Details',
      items: [{ id: 'jobForm', label: 'Job Form' }],
    },
    {
      label: 'Delivery',
      items: [
        { id: 'jobsheet', label: 'Jobsheet' },
        { id: 'variations', label: 'Variations', count: variations.length },
        { id: 'workings', label: 'Workings' },
        { id: 'siteReports', label: 'Site Reports' },
      ],
    },
    ...(jobNotifiableWorks.length > 0
      ? [
          {
            label: 'Notifiable Works',
            // One item per notifiable work, all opening the same tab on that work.
            items: jobNotifiableWorks.map((work) => ({
              id: 'notifiableWorks',
              key: `nw-${work.name}`,
              label: work.name,
              notifiableWork: work,
            })),
          },
        ]
      : []),
    {
      label: 'Financial',
      items: [
        { id: 'invoices', label: 'Invoices', count: formState.invoiceItems.length },
        ...(hasPurchaseOrderPermission
          ? [{ id: 'purchaseOrders', label: 'Purchase Orders', count: purchaseOrderCount }]
          : []),
        { id: 'accruals', label: 'Accruals', count: accruals.length },
        { id: 'retentionsPayable', label: 'Retentions Payable', count: retentionsPayable.length },
        { id: 'subcontracts', label: 'Sub-Contracts' },
      ],
    },
    {
      label: 'Attachments',
      items: [
        ...(props.lockEdit ? [] : [{ id: 'files', label: 'Files' }]),
        { id: 'notes', label: 'Notes' },
        { id: 'links', label: 'Links' },
      ],
    },
  ]

  return loading ? (
    <SkeletonForm />
  ) : (
    <div className='form__wrapper'>
      <div style={{ display: 'flex', width: '100%', flex: 1, minHeight: 0, height: '100%' }}>
        <nav className='jobnav__rail'>
          {jobNavGroups.map((group) => (
            <div key={group.label} className='jobnav__group'>
              <div className='jobnav__group-label'>{group.label}</div>
              {group.items.map((item) => {
                const idx = jobTabOrder.indexOf(item.id)
                const work: JobNotifiableWork | undefined = (item as any).notifiableWork
                const active = selectedTab === idx && (!work || selectedNotifiableWork === work.name)
                return (
                  <button
                    key={(item as any).key ?? item.id}
                    type='button'
                    className={`jobnav__item${active ? ' active' : ''}`}
                    onClick={() => {
                      if (work) {
                        setSelectedNotifiableWork(work.name)
                        // Picks up anything uploaded since, e.g. from the Files tab.
                        loadJobNotifiableWorks()
                      }
                      setSelectedTab(idx)
                    }}
                  >
                    <span className='jobnav__item-label'>{item.label}</span>
                    {work ? (
                      // Only required documents count; a work with none has no badge.
                      work.requiredCount === 0 ? null : work.outstanding > 0 ? (
                        <span
                          className='jobnav__badge jobnav__badge--warn'
                          title={`${work.outstanding} required document${work.outstanding === 1 ? '' : 's'} still to do`}
                        >
                          {work.outstanding}
                        </span>
                      ) : (
                        <span className='jobnav__badge jobnav__badge--done' title='All required documents done'>
                          <FaCheck aria-label='All required documents done' />
                        </span>
                      )
                    ) : (
                      typeof (item as any).count === 'number' && (item as any).count > 0 && (
                        <span className='jobnav__badge'>{(item as any).count}</span>
                      )
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </nav>
        <TabStrip
          selected={selectedTab}
          onSelect={handleSelectedTab}
          className='form__tabstrip__container jobnav__tabstrip'
          animation={false}
        >
          <TabStripTab title='Job Form'>
            <div style={{ paddingTop: '1rem' }}>
              <TabSectionHeader
                icon={<FaBriefcase />}
                title="Job Details"
                description="Core details and settings for this job"
                actions={
                  <>
                    <button
                      type="button"
                      onClick={() => window.open(`/job-costing?id=${jobId}&jid=${formState.refFormatted}`, '_blank')}
                      className="jobsection__action-btn"
                    >
                      Job Costing
                    </button>
                    {hasFinancialStatementPermission && (
                      <button
                        type="button"
                        onClick={() => window.open(`/jobs/${jobId}/financial-statement`, '_blank')}
                        className="jobsection__action-btn"
                      >
                        Financial Statement
                      </button>
                    )}
                  </>
                }
              />
            </div>
            <Form
              initialValues={{
                description: formState.description,
                purchaseOrder: formState.purchaseOrder,
                externallyBooked: formState.externallyBooked,
                externallyBookedName: formState.externallyBookedName,
                isExported: formState.isExported,
                firstHourInvoiced: formState.firstHourInvoiced,
                inDefect: formState.inDefect,
                enableSubcontractAgreements: formState.enableSubcontractAgreements,
                jobEmployeeIds: formState.jobEmployees,
              }}
              onSubmit={handleSubmit}
              render={(formRenderProps) => (
                <FormElement
                  style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }}
                >
                  <fieldset className={'k-form-fieldset'}>

                    {(formState.quotes.length > 0 || formState.enquiryRef) && (
                      <div className='jobmeta__chips'>
                        {formState.quotes.length === 1 && (
                          <span className='jobmeta__chip' onClick={() => window.open(`/quotes/?id=${formState.quotes[0].quoteId}`, "_blank")}>
                            Quote {formState.quotes[0].refFormatted}
                          </span>
                        )}
                        {formState.quotes.length > 1 && (
                          <QuotesPopup quotes={formState.quotes} />
                        )}
                        {formState.enquiryRef ? <span className='jobmeta__chip' onClick={() => window.open(`/enquiries/?id=${formState.enquiryId}`, "_blank")}>Enquiry E{formState.enquiryRef}</span> : null}
                      </div>
                    )}

                    <div className='form__details__subcontainer'>
                      <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Contact: {`${formState.contact?.title || ""} ${formState.contact?.firstname || ""} ${formState.contact?.lastname || ""}`}</p>
                      <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Address: {`${formState.site.siteAddr || ""}`}</p>
                      <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Phone: {formState.contact.phone || ""}</p>
                      <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Email: <a href={`mailto:${formState.contact.email}`}>{formState.contact.email}</a></p>
                    </div>

                    <Divider>
                      General Info
                    </Divider>

                    <Field
                      disabled={props.lockEdit}
                      data={clientData}
                      originalData={clientDataOrig}
                      name={'clientName'}
                      component={FormDropDownListFilter}
                      label={'Choose a client:'}
                      setData={setClientData}
                      searchDropdown={searchClientDropdown}
                      defaultItem={formState.clientName}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          clientName: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : true}
                      buttonText={'Add Account'}
                      buttonAction={() => {
                        props.setAddAccountVisible(true)
                        props.setAccountMode("add");
                        props.setAccountInitialTab(0);
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      data={contactsForClient}
                      originalData={siteDataOrig}
                      name={'contact'}
                      required={true}
                      component={FormDropDownListFilter}
                      label={'Contact:'}
                      setData={setContactsForClient}
                      onOpen={() => getAndStoreContactsForCustomer(formState.clientName.value)}
                      searchDropdown={searchSiteDropdown}
                      defaultItem={contactsForClient.find(item => Number(item.value) === Number(formState.contact.contactId))}
                      onChange={(e) => {
                        const contactId = parseInt(e.value.value, 10);
                        refreshContactDetails(contactId);
                        setFormState({
                          ...formState,
                          contact: {
                            ...e.value,
                            contactId,
                          }
                        })
                      }}
                      hasButton={props.lockEdit ? false : formState.clientName.value > 0}
                      buttonText={'Add Contact'}
                      buttonAction={() => {
                        props.setAccountMode("edit");
                        props.setSelectedAccountId(formState.clientName.value);
                        props.setAccountInitialTab(2);
                        props.setAddAccountVisible(true)
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      data={siteData}
                      required={true}
                      originalData={siteDataOrig}
                      name={'address'}
                      component={FormDropDownList}
                      label={'Address:'}
                      setData={setSiteData}
                      onOpen={() => getAndStoreAddressesForCustomer(formState.clientName.value)}
                      searchDropdown={searchSiteDropdown}
                      defaultItem={siteData.find(item => Number(item.value) === Number(formState.siteAddr.value))}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          siteAddr: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : formState.clientName.value > 0}
                      buttonText={'Add Address'}
                      buttonAction={() => {
                        props.setAccountMode("edit");
                        props.setSelectedAccountId(formState.clientName.value);
                        props.setAccountInitialTab(1);
                        props.setAddAccountVisible(true)
                      }}
                    />

                    {/* <Field
                      disabled={props.lockEdit}
                      data={clientData}
                      originalData={clientDataOrig}
                      name={'clientName'}
                      component={FormDropDownListFilter}
                      label={'Choose a client:'}
                      setData={setClientData}
                      searchDropdown={searchClientDropdown}
                      defaultItem={formState.clientName}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          clientName: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : true}
                      buttonText={'Add Client'}
                      buttonAction={handleAddClient}
                    />

                    <Field
                      disabled={props.lockEdit}
                      data={siteData}
                      originalData={siteDataOrig}
                      name={'siteAddr'}
                      component={FormDropDownListFilter}
                      label={'Choose a site:'}
                      setData={setSiteData}
                      searchDropdown={searchSiteDropdown}
                      defaultItem={formState.siteAddr}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          siteAddr: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : true}
                      buttonText={'Add Site'}
                      buttonAction={handleAddSite}
                    /> */}

                    <Field
                      disabled={props.lockEdit}
                      data={enquirysourceData}
                      name={'enquirysourceId'}
                      component={FormDropDownList}
                      label={'Choose enquiry source:'}
                      setData={setEnquirysourceData}
                      defaultItem={formState.enquirysourceId}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          enquirysourceId: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      name={'description'}
                      component={FormTextArea}
                      label={'Add job description:'}
                      defaultValue={formState.description}
                      onChange={(e) => {
                        setFormState({ ...formState, description: e.value })
                      }}
                    />

                    {isBadDebter && (
                      <div style={{ 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffc107', 
                        borderRadius: '4px', 
                        padding: '12px', 
                        marginTop: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaFlag style={{ color: '#856404' }} />
                        <span style={{ color: '#856404', fontWeight: 'bold' }}>
                          Bad Debter - Contact accounts department
                        </span>
                      </div>
                    )}

                    <Divider>
                      Reminders
                    </Divider>
                    <JobsRemidners
                      reminders={jobReminders}
                      handleCompleteReminder={handleCompleteReminder} />
                    <Divider>
                      Job Details
                    </Divider>

                    <div style={{ marginBottom: '1rem' }}>
                      <Label>Notifiable Works:</Label>
                      <MultiSelect
                        disabled={props.lockEdit}
                        data={notifiableWorksOptions}
                        value={formState.notifiableWorks}
                        onChange={(e) => setFormState({ ...formState, notifiableWorks: e.value })}
                        placeholder="Select notifiable works..."
                      />
                    </div>

                    <Field
                      disabled={props.lockEdit}
                      data={worktypeData}
                      name={'worktypeId'}
                      component={FormDropDownList}
                      label={'Choose the type of work:'}
                      defaultItem={formState.worktypeId}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          worktypeId: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : true}
                      buttonText={'Add Work Type'}
                      buttonAction={handleAddWorktype}
                    />

                    <Field
                      disabled={props.lockEdit}
                      name={'startDate'}
                      component={FormDateTimePicker}
                      label={'Choose start date:'}
                      defaultText={formState.startDate}
                      placeholder={dateTimeFormatter.formatDateTime(formState.startDate)}
                      onChange={(e) => {
                        setFormState({ ...formState, startDate: e.value })
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      name={'endDate'}
                      component={FormDateTimePicker}
                      label={'Choose end date:'}
                      defaultText={formState.endDate}
                      placeholder={dateTimeFormatter.formatDateTime(formState.endDate)}
                      onChange={(e) => {
                        setFormState({ ...formState, endDate: e.value })
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      data={employeeProjectData}
                      originalData={employeeDataOrig}
                      name={'employeeProjectmanager'}
                      component={FormDropDownListFilter}
                      searchDropdown={searchEmployeeProjectDropdown}
                      label={'Choose project manager:'}
                      setData={setEmployeeData}
                      defaultItem={formState.employeeProjectmanager}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          employeeProjectmanager: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                    />

                    <Field
                      disabled={props.lockEdit}
                      data={employeeData}
                      originalData={employeeDataOrig}
                      name={'jobEmployeeIds'}
                      component={FormMultiSelect}
                      searchDropdown={searchEmployeeDropdown}
                      label={'Choose assigned employees:'}
                      defaultValue={formState?.jobEmployees}
                      onChange={(e) => {
                        const updatedJobEmployeeIds = e.value.map((item: any) => ({
                          value: parseInt(item.value, 10),
                          text: item.text,
                        }))

                        setFormState({
                          ...formState,
                          jobEmployees: updatedJobEmployeeIds,
                        })
                      }}
                    />
                    <br />
                    <Field
                      disabled={props.lockEdit}
                      name={'externallyBooked'}
                      component={FormCheckbox}
                      label={'Externally Booked:'}
                      defaultValue={formState.externallyBooked}
                      onChange={(e) => {
                        setFormState({ ...formState, externallyBooked: e.value })
                      }}
                    />
                    <Field
                      disabled={props.lockEdit}
                      name={'externallyBookedName'}
                      component={FormInput}
                      label={'Externally Booked Name:'}
                      placeholder={'Enter external booking name:'}
                      defaultValue={formState.externallyBookedName}
                      onChange={(e) => {
                        setFormState({ ...formState, externallyBookedName: e.value })
                      }}
                    />
                    <br />
                    <Label>Booked Start Date:</Label>
                    {formatScheduleDate(formState.bookedStartDate)}
                    <br />

                    <br />
                    <Label>Booked End Date:</Label>
                    {formatScheduleDate(formState.bookedEndDate)}
                    <br />
                    <Field
                      disabled={props.lockEdit}
                      name={'inDefect'}
                      component={FormCheckbox}
                      label={'In Defect:'}
                      defaultValue={formState.inDefect}
                      onChange={(e) => {
                        setFormState({ ...formState, inDefect: e.value })
                      }}
                    />
                    <Divider>
                      Invoice Details
                    </Divider>

                    <Field
                      disabled={props.lockEdit}
                      name={'purchaseOrder'}
                      component={FormInput}
                      label={'Purchase Order/Works Order:'}
                      placeholder={'Enter a PO/WO number:'}
                      defaultValue={formState.purchaseOrder}
                      onChange={(e) => {
                        setFormState({ ...formState, purchaseOrder: e.value })
                      }}
                    />

                    <div style={{ display: "flex", flexDirection: "row", gap: "50px" }}>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                        <Field
                          disabled={true}
                          name={'cost'}
                          component={FormNumericTextBox}
                          label={'Original Quote:'}
                          placeholder={`${formState.cost}`}
                          onChange={(e) => {
                            setFormState({ ...formState, cost: e.value })
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                        <Field
                          disabled={true}
                          name={'variationsTotal'}
                          component={FormNumericTextBox}
                          label={'Variations:'}
                          placeholder={`${variations.reduce((sum, variation) => sum + (variation.cost || 0), 0).toFixed(2)}`}
                          onChange={(e) => {
                            // Read-only field, no onChange needed
                          }}
                        />
                        
                      </div>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                        <Field
                          disabled={true}
                          name={'totalCost'}
                          component={FormNumericTextBox}
                          label={'Total Job Value:'}
                          placeholder={`${formState.cost + variations.reduce((sum, variation) => sum + (variation.cost || 0), 0)}`}
                          onChange={(e) => {
                            // Read-only field showing cost + variations total
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', margin: '2rem 0 1rem', justifyContent: 'space-between' }}>
                      <div>
                        <label className="k-label">Costs to date:</label>
                        <div className="k-input-value-text">
                          £{(formState.costsToDate.toFixed(2) ?? 0).toLocaleString()}
                        </div>
                      </div>
                      <div title="Still to come on this job's purchase orders that haven't fully arrived, before VAT. A part delivered line counts only what's outstanding.">
                        <label className="k-label">Committed costs:</label>
                        <div className="k-input-value-text">
                          £{(formState.committedCosts ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <label className="k-label">Amount billed to date:</label>
                        <div className="k-input-value-text">
                          £{(formState.amountBilledToDate.toFixed(2) ?? 0).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <label className="k-label">Amount left to bill:</label>
                        <div className="k-input-value-text">
                          {((formState.cost + variations.reduce((sum, variation) => sum + (variation.cost || 0), 0)) - formState.amountBilledToDate).toFixed(2).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* First hour invoices are raised from the job in the mobile app;
                        here the job only shows that one has been. */}
                    {
                      formState.firstHourInvoiced && (
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", margin: "2em 0.5em 0 0.5em", border: "1px solid rgb(129 192 35)", borderRadius: "0.3em", padding: "1em" }}>
                          <span
                            style={{
                              height: "8px",
                              width: "8px",
                              backgroundColor: "rgb(129 192 35)",
                              borderRadius: "50%",
                              display: "inline-block",
                              marginRight: "8px",
                            }}
                          ></span>
                          Invoice has been created for the first hour.
                        </div>
                      )
                    }

                    {
                      //   ! projectUrlAlreadySet && (
                      //     <Field
                      //       name={'folderStructure'}
                      //       component={FormSwitch}
                      //       label={'Create folder structure'}
                      //       defaultText={formState.projectFolderUrl === null ? false : true}
                      //       placeholder={formState.projectFolderUrl === null ? false : true}
                      //       defaultValue={formState.projectFolderUrl === null ? false : true}
                      //       onChange={(e) => {
                      //         setFormState({ ...formState, projectFolderUrl: null })
                      //         setFolderStructureToggleSet(e.value)
                      //       }}
                      //     />
                      //  )

                    }

                    {
                      !projectUrlAlreadySet && props.lockEdit == false ? (
                        <Field
                          disabled={props.lockEdit}
                          data={[
                            { text: "M&E", value: 1 },
                            { text: "Construction", value: 2 }
                          ]}
                          name={'selectStructure'}
                          component={FormDropDownList}
                          label={'Select folder structure'}
                          onChange={(e) => {
                            setFormState({ ...formState, projectFolderUrl: e.value.text === undefined ? null : e.value.text })
                          }}
                        />
                      ) : null
                    }

                    {
                      projectUrlAlreadySet && (
                        <>
                          <br />
                          <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label">Folder structure</label>
                          {checkForProjectUrl()}
                        </>

                      )
                    }

                    {isDeleted && props.lockEdit === false ? (
                      <div className='button__container'>
                        {
                          buttonSpinner ? (
                            <button disabled={true} className='form__edit__button edit'>
                              <Loader size="medium" type="infinite-spinner" style={{ color: "rgb(71, 60, 7)" }} />
                            </button>
                          ) : (
                            <button
                              onClick={reinstateDeletedJobHandler}
                              className='form__edit__button edit'
                            >
                              Reinstate Job
                            </button>
                          )
                        }
                      </div>
                    ) : null}

                    {isCompleted && props.lockEdit === false ? (
                      <div className='button__container'>
                        {
                          buttonSpinner ? (
                            <button disabled={true} className='form__edit__button edit'>
                              <Loader size="medium" type="infinite-spinner" style={{ color: "rgb(71, 60, 7)" }} />
                            </button>
                          ) : (
                            <button
                              onClick={reinstateCompletedJobHandler}
                              className='form__edit__button edit'
                            >
                              Reinstate Job
                            </button>
                          )
                        }
                      </div>
                    ) : null}

                    {!isDeleted && !isCompleted && props.lockEdit === false ? (
                      <div className='button__container'>
                        {
                          buttonSpinner ? (
                            <button disabled={true} className='form__edit__button edit'>
                              <Loader size="medium" type="infinite-spinner" style={{ color: "rgb(71, 60, 7)" }} />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                handleSubmit(formState)
                              }}
                              disabled={formState.clientName.value === 0 || formState.siteAddr.value === 0 || formState.contact.contactId === 0}
                              className='form__edit__button edit'
                            >
                              Save changes to Job
                            </button>
                          )
                        }
                        {
                          buttonSpinner ? (
                            <button disabled={true} className='form__edit__button accept'>
                              <Loader size="medium" type="infinite-spinner" style={{ color: "rgb(39, 46, 10)" }} />
                            </button>
                          ) : (
                            <button onClick={() => completeJobHandler(true)} className='form__edit__button accept'>
                              Mark Job as Completed
                            </button>
                          )
                        }
                        {
                          buttonSpinner ? (
                            <button disabled={true} className='form__edit__button reject'>
                              <Loader size="medium" type="infinite-spinner" style={{ color: "rgb(58, 15, 15)" }} />
                            </button>
                          ) : (
                            <button onClick={deleteJobHandler} className='form__edit__button reject'>
                              Mark Job as Deleted
                            </button>
                          )
                        }
                      </div>
                    ) : null}
                  </fieldset>
                </FormElement>
              )}
            />
          </TabStripTab>
          <TabStripTab title='Jobsheet'>
            <TabSectionHeader
              icon={<FaFileAlt />}
              title="Jobsheet"
              description="Jobsheets and completion details for this job"
              actions={
                !isDeleted && !isCompleted ? (
                  <button
                    type="button"
                    onClick={handleCreateQuote}
                    disabled={buttonSpinner}
                    className="jobsection__action-btn accent"
                  >
                    {buttonSpinner ? (
                      <Loader size="small" type="infinite-spinner" style={{ color: '#fff' }} />
                    ) : (
                      'Create Quote'
                    )}
                  </button>
                ) : undefined
              }
            />
            <div style={{ padding: '0 1rem 1rem' }}>
            {
              formState.jobsheets.length > 0 ? (
                formState.jobsheets.map((jobsheet: Jobsheet) => (
                  <div style={{ marginBottom: "20px" }} className="form__details__subcontainer">
                    {jobsheet.highPriority && (
                      <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <FaExclamationTriangle 
                          style={{ 
                            color: "#e9540d", 
                            fontSize: "16px" 
                          }} 
                          title="High Priority Jobsheet"
                        />
                        <span style={{ 
                          color: "#e9540d", 
                          fontWeight: "bold", 
                          fontSize: "12pt" 
                        }}>
                          HIGH PRIORITY JOBSHEET
                        </span>
                      </div>
                    )}
                    <div style={{ marginBottom: "10px" }}>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Works Completed:</strong></label>
                      <div className={"k-input-value-text"}>{jobsheet.worksCompleted}</div>
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Materials:</strong></label>
                      <div className={"k-input-value-text"}>{jobsheet.materials}</div>
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Any Remedial Works Required:</strong></label>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", margin: "2px 0 6px" }}>
                        {jobsheet.remedialWorksRequired
                          ? <FaCheckSquare style={{ color: "#4b8b3b", fontSize: "14px" }} />
                          : <FaRegSquare style={{ color: "#8a8a8a", fontSize: "14px" }} />}
                        <span className={"k-input-value-text"}>
                          {jobsheet.remedialWorksRequired ? "Remedial works required" : "No remedial works required"}
                        </span>
                      </div>
                      {jobsheet.remedialWorksRequired && (
                        <>
                          <div className={"k-input-value-text"}>{jobsheet.remedialWorks || "-"}</div>

                          <div style={{ marginTop: "10px" }}>
                            <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Estimated Labour:</strong></label>
                            <div className={"k-input-value-text"}>{formatRemedialLabour(jobsheet)}</div>
                          </div>

                          <div style={{ marginTop: "10px" }}>
                            <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Estimated Materials:</strong></label>
                            {jobsheet.remedialMaterials && jobsheet.remedialMaterials.length > 0 ? (
                              <ul style={{ margin: "4px 0 0", paddingLeft: "1.2rem" }}>
                                {jobsheet.remedialMaterials.map((material) => (
                                  <li key={material.jobsheetRemedialMaterialId} className={"k-input-value-text"}>
                                    {formatQuantity(material.quantity)} &times; {material.description}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className={"k-input-value-text"}>-</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Created Date:</strong></label>
                      <div className={"k-input-value-text"}>{dayjs(jobsheet.createdDate).format("DD-MM-YYYY")}</div>
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Employee:</strong></label>
                      <div className={"k-input-value-text"}>{jobsheet.employee?.employeeName}</div>
                    </div>
                    {
                      jobsheet.documents.length ? (
                        <div style={{ marginBottom: "10px" }}>
                          <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Documents:</strong></label>
                          <div className={""}>
                            <ul>
                              {
                                jobsheet.documents.map(document => (
                                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span
                                      title="View this file"
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={async () => { await getDocument(document.documentId, document.fileName) }}
                                    >{document.fileName}</span>
                                    <button
                                      type="button"
                                      title="Download file"
                                      onClick={async () => { await downloadDocument(document.documentId, document.fileName) }}
                                      style={{ background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer', fontSize: '14px', color: '#1e2a4e' }}
                                    >
                                      ⬇
                                    </button>
                                  </li>
                                ))
                              }
                            </ul>
                          </div>
                        </div>
                      ) : null
                    }
                    <div>
                      <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>Mark As Actioned:</strong></label>
                      <div className={"k-input-value-text"}><Switch
                        checked={jobsheet.isProcessed}
                        onChange={(e) => {
                          setJobsheetAsProcessed(jobsheet.jobsheetId)
                        }} />
                      </div>
                    </div>
                  </div>
                ))
              ) : null
            }
            </div>
          </TabStripTab>
          <TabStripTab title='Invoices'>
            <TabSectionHeader
              icon={<FaFileInvoiceDollar />}
              title="Invoices"
              description="Invoices raised against this job"
            />
            <div style={{ padding: '0 1rem 1rem' }}>
            {
              formState.invoiceItems.length ? (
                formState.invoiceItems.map(item => (
                  <div style={{ marginBottom: "10px", cursor: "pointer" }} className="form__details__subcontainer" onClick={() => window.open(`/invoicing?id=${item.invoiceId}`, "_blank")}>
                  <label id="undefined_label" style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label"><strong>I{item.invoiceRef}</strong></label>
                  <div className={"k-input-value-text"}>{dayjs(item.invoiceDate).format("DD/MM/YYYY")} - £{item.itemValue}</div>
                </div>

                ))
              ) : <div>No invoices to show</div>
            }
            </div>
          </TabStripTab>
          {
            hasPurchaseOrderPermission ? (
              <TabStripTab title='Purchase Orders'>
                <JobPurchaseOrders
                  jobId={jobId}
                  lockEdit={props.lockEdit}
                  onCountChange={setPurchaseOrderCount}
                />
              </TabStripTab>
            ) : null
          }
          {
            props.lockEdit === false ? (
              <TabStripTab title='File Upload'>
                <TabSectionHeader
                  icon={<FaPaperclip />}
                  title="Files"
                  description="Upload and manage documents for this job"
                />
                <div style={{ padding: '0 1rem 1rem' }}>
                <ExternalDropZone
                  style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }}
                  uploadRef={uploadRef}
                />
                <div style={{ height: '20px' }} />
                <Upload
                  ref={uploadRef}
                  batch={true}
                  files={files}
                  multiple={true}
                  defaultFiles={[]}
                  withCredentials={false}
                  listItemUI={CustomListItemUI}
                  saveHeaders={{ Authorization: `Bearer ${token}` }}
                  saveUrl={token ? `https://${apiURL}/Files/Job/${jobId}` : ''}
                  onStatusChange={handleUploadSuccess}
                />

                <div style={{ height: '1.5rem' }} />

                <div>
                  {documentData.length > 0 ? (
                    <div>
                      {documentData.map((document: fileDocumentTypes) => (
                        <QuoteDocumentRow
                          key={document.documentId}
                          onDocumentId={document.documentId}
                          onFileName={document.fileName}
                          onFileExtension={document.fileExtension}
                          onDocumentDelete={handleDocumentDelete}
                          onJobId={jobId}
                          onQuoteId={quoteId}
                          appCanView={document.appCanView}
                          isSurveyFile={document.isSurveyFile}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        padding: '1rem',
                        backgroundColor: 'rgb(216 229 237)',
                        borderRadius: '0.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <p style={{ margin: 0 }}>No files uploaded yet</p>
                    </div>
                  )}
                </div>
                </div>
              </TabStripTab>
            ) : null
          }
          <TabStripTab title="Notes">
            <TabSectionHeader
              icon={<FaRegStickyNote />}
              title="Notes"
              description="Internal notes and comments for this job"
            />
            <Notes lockEdit={props.lockEdit} sourceEntity={addNoteDTO} />
          </TabStripTab>
          <TabStripTab title="Links">
            <TabSectionHeader
              icon={<FaLink />}
              title="Links"
              description="Related web links and resources for this job"
            />
            <EntityLinks lockEdit={props.lockEdit} sourceEntity={addNoteDTO} />
          </TabStripTab>
          <TabStripTab title="Variations">
            <JobVariations
              variations={variations}
              quotes={formState.quotes}
              originalQuoteId={formState.quote?.quoteId || null}
              jobId={jobId}
              lockEdit={props.lockEdit}
              onVariationsChange={(updatedVariations) => {
                setVariations(updatedVariations);
                setFormState(prev => ({ ...prev, variations: updatedVariations }));
              }}
            />
          </TabStripTab>
          <TabStripTab title="Workings">
            <TabSectionHeader
              icon={<FaHammer />}
              title="Workings"
              description="Material and labour breakdown for this job"
              actions={
                <button
                  type="button"
                  onClick={() => workingSectionsRef.current?.openAddSection()}
                  className="jobsection__action-btn"
                >
                  Add Section
                </button>
              }
            />
            <div style={{ padding: '0 1rem 1rem' }}>
              {/* Workings grouped into sections (each section holds both material and labour workings) */}
              <WorkingSectionsView
                ref={workingSectionsRef}
                hideToolbar
                jobId={jobId}
                lockEdit={false}
                materialWorkings={materialWorkings}
                onMaterialWorkingsChange={setMaterialWorkings}
                labourWorkings={labourWorkings}
                onLabourWorkingsChange={setLabourWorkings}
              />

              <WorkingsAdjustmentSummary
                materialWorkings={materialWorkings}
                labourWorkings={labourWorkings}
                adjustmentPercent={formState.workingsAdjustmentPercent}
                onAdjustmentPercentChange={(value) =>
                  setFormState({ ...formState, workingsAdjustmentPercent: value })
                }
                lockEdit={props.lockEdit}
              />
            </div>
          </TabStripTab>
          
          <TabStripTab title="Accruals">
            <TabSectionHeader
              icon={<FaCoins />}
              title="Accruals"
              description="Accrued costs recorded against this job"
              actions={
                !props.lockEdit ? (
                  <button
                    type="button"
                    onClick={handleAddAccrual}
                    className="jobsection__action-btn"
                  >
                    Add Accrual
                  </button>
                ) : undefined
              }
            />
            <AccrualsGrid
              accruals={accruals}
              onAdd={handleAddAccrual}
              onEdit={handleEditAccrual}
              onDelete={handleDeleteAccrual}
              lockEdit={props.lockEdit}
            />
          </TabStripTab>

          <TabStripTab title="Retentions Payable">
            <TabSectionHeader
              icon={<FaHandHoldingUsd />}
              title="Retentions Payable"
              description="Retentions payable recorded against this job"
              actions={
                !props.lockEdit ? (
                  <button
                    type="button"
                    onClick={handleAddRetentionPayable}
                    className="jobsection__action-btn"
                  >
                    Add Retention Payable
                  </button>
                ) : undefined
              }
            />
            <RetentionsPayableGrid
              retentionsPayable={retentionsPayable}
              onAdd={handleAddRetentionPayable}
              onEdit={handleEditRetentionPayable}
              onDelete={handleDeleteRetentionPayable}
              lockEdit={props.lockEdit}
            />
          </TabStripTab>

          <TabStripTab title="Site Reports">
            <TabSectionHeader
              icon={<FaClipboardList />}
              title="Site Reports"
              description="On-site reports captured for this job"
              actions={
                <>
                  {!props.lockEdit && (
                    <button
                      type="button"
                      onClick={() => siteReportsRef.current?.addReport()}
                      className="jobsection__action-btn"
                    >
                      New Site Report
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => siteReportsRef.current?.exportToExcel()}
                    className="jobsection__action-btn"
                  >
                    Export to Excel
                  </button>
                </>
              }
            />
            <SiteReportsTab ref={siteReportsRef} hideActions jobId={jobId} lockEdit={props.lockEdit} />
          </TabStripTab>

          <TabStripTab title="Sub-Contracts">
            <SubContracts jobId={jobId} lockEdit={props.lockEdit} />
          </TabStripTab>

          <TabStripTab title="Notifiable Works">
            <JobNotifiableWorks
              jobId={jobId}
              work={jobNotifiableWorks.find((w) => w.name === selectedNotifiableWork) ?? null}
              lockEdit={props.lockEdit}
              onChanged={loadJobNotifiableWorks}
            />
          </TabStripTab>

        </TabStrip>
      </div>
      {
        accrualDialogVisible && (
          <Dialog
            title={selectedAccrual ? "Edit Accrual" : "Add New Accrual"}
            onClose={handleAccrualCancel}
            appendTo={document.body}
            className="accrual-dialog"
            width={600}
          >
            <AccrualForm
              accrual={selectedAccrual}
              jobId={jobId}
              onSubmit={handleAccrualSubmit}
              onCancel={handleAccrualCancel}
            />
          </Dialog>
        )
      }
      {
        retentionPayableDialogVisible && (
          <Dialog
            title={selectedRetentionPayable ? "Edit Retention Payable" : "Add New Retention Payable"}
            onClose={handleRetentionPayableCancel}
            appendTo={document.body}
            className="retention-payable-dialog"
            width={600}
          >
            <RetentionPayableForm
              retentionPayable={selectedRetentionPayable}
              jobId={jobId}
              onSubmit={handleRetentionPayableSubmit}
              onCancel={handleRetentionPayableCancel}
            />
          </Dialog>
        )
      }
      {
        bookingCheckDialogVisible && (
          <Dialog title={"Future Diary Bookings"} onClose={() => {
            setBookingCheckDialogVisible(false)
            setHasFutureBookings(false)
          }}>
            <p
              style={{
                margin: "25px",
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: "15px" }}>
                <FaExclamationTriangle style={{ color: "orange", marginRight: "10px" }} />
                This job has future diary bookings scheduled.
              </div>
            </p>
            <DialogActionsBar>
             <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={() => {
                  setBookingCheckDialogVisible(false)
                  setHasFutureBookings(false)
                }}
              >
                Cancel
              </button>
            </DialogActionsBar>
          </Dialog>
        )
      }
      {
        reminderDialogVisible && (
          <Dialog title={"Please confirm"} onClose={() => setReminderDialogVisible(false)}>
            <p
              style={{
                margin: "25px",
                textAlign: "center",
              }}
            >
              <div>Do you want to create a new Service Reminder for this client, site and customer?</div>
            </p>
            <DialogActionsBar>
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={() => {
                  setReminderDialogVisible(false)
                  props.onCompleteJobSubmit(jobId)
                  redirectToCreateNewServiceReminder()
                }}
              >
                Yes and mark as complete
              </button>
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={() => {
                  setReminderDialogVisible(false)
                  props.onCompleteJobSubmit(jobId)
                }}
              >
                No, just mark as complete
              </button>
            </DialogActionsBar>
          </Dialog>
        )
      }
    </div>
  )
}

export default EditJobForm
